import {
  Search,
  FilterList,
  Sort,
  PictureAsPdf,
  Image as ImageIcon,
  Description,
  CalendarToday,
  Download,
} from "@mui/icons-material";
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Card,
  CardContent,
  IconButton,
  Menu,
  SelectChangeEvent,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import { format, startOfDay, endOfDay } from "date-fns";
import React, { useState, useMemo, useEffect, useCallback } from "react";

import { patientGeneticsApi } from "apis";
import {
  GeneticsReportResponseDto,
  GeneticsReportResponseDtoFileTypeEnum,
} from "libs/cellera-sdk";
import {
  GetGeneticsReportsByPatientSortByEnum,
  GetGeneticsReportsByPatientOrderByEnum,
  GetGeneticsReportsByPatientFileTypeEnum,
} from "libs/cellera-sdk/apis/PatientGeneticsReportsApi";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface PatientGeneticsProps {
  patientId: string;
}

type SortField = GetGeneticsReportsByPatientSortByEnum;
type SortOrder = GetGeneticsReportsByPatientOrderByEnum;
type FileTypeFilter = GetGeneticsReportsByPatientFileTypeEnum | "all";

function PatientGenetics({ patientId }: PatientGeneticsProps) {
  const { colors } = useCelleraTheme();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortField>(
    GetGeneticsReportsByPatientSortByEnum.UploadedAt,
  );
  const [orderBy, setOrderBy] = useState<SortOrder>(
    GetGeneticsReportsByPatientOrderByEnum.Desc,
  );
  const [fileTypeFilter, setFileTypeFilter] = useState<FileTypeFilter>("all");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);

  const isFilterMenuOpen = Boolean(filterAnchorEl);
  const isSortMenuOpen = Boolean(sortAnchorEl);

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch genetics reports
  const { data, isLoading, error } = useQuery({
    queryKey: [
      "geneticsReports",
      patientId,
      debouncedSearchTerm,
      sortBy,
      orderBy,
      fileTypeFilter,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => {
      const params: Parameters<
        typeof patientGeneticsApi.getGeneticsReportsByPatientId
      >[0] = {
        patientId,
        search: debouncedSearchTerm || undefined,
        sortBy,
        orderBy,
        fileType: fileTypeFilter !== "all" ? fileTypeFilter : undefined,
        uploadDateFrom: dateFrom ? startOfDay(dateFrom) : undefined,
        uploadDateTo: dateTo ? endOfDay(dateTo) : undefined,
        page: 1,
        limit: 100,
      };
      return patientGeneticsApi.getGeneticsReportsByPatientId(params);
    },
    enabled: Boolean(patientId),
  });

  const reports = useMemo(() => data?.data || [], [data?.data]);
  const totalResults = useMemo(
    () => data?.pagination?.total || 0,
    [data?.pagination?.total],
  );

  // Get file type icon
  const getFileTypeIcon = useCallback(
    (fileType: GeneticsReportResponseDtoFileTypeEnum) => {
      switch (fileType) {
        case GeneticsReportResponseDtoFileTypeEnum.Pdf:
          return <PictureAsPdf sx={{ color: colors.error[500] }} />;
        case GeneticsReportResponseDtoFileTypeEnum.Word:
          return <Description sx={{ color: colors.info[500] }} />;
        case GeneticsReportResponseDtoFileTypeEnum.Image:
          return <ImageIcon sx={{ color: colors.success[500] }} />;
        default:
          return <Description sx={{ color: colors.text.secondary }} />;
      }
    },
    [colors],
  );

  // Get file type label
  const getFileTypeLabel = useCallback(
    (fileType: GeneticsReportResponseDtoFileTypeEnum) => {
      switch (fileType) {
        case GeneticsReportResponseDtoFileTypeEnum.Pdf:
          return "PDF";
        case GeneticsReportResponseDtoFileTypeEnum.Word:
          return "Word";
        case GeneticsReportResponseDtoFileTypeEnum.Image:
          return "Image";
        default:
          return "Unknown";
      }
    },
    [],
  );

  // Format file size to MB
  const formatFileSize = useCallback((bytes: number | undefined): string => {
    if (!bytes || bytes === 0) return "N/A";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  }, []);

  // Handle download
  const handleDownload = useCallback(
    (fileUrl: string | undefined, fileName: string) => {
      if (!fileUrl) {
        console.error("No file URL available for download");
        return;
      }

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [],
  );

  // Handle search input change
  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    [],
  );

  // Handle sort menu
  const handleSortMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setSortAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleSortMenuClose = useCallback(() => {
    setSortAnchorEl(null);
  }, []);

  const handleSortChange = useCallback((field: SortField, order: SortOrder) => {
    setSortBy(field);
    setOrderBy(order);
    setSortAnchorEl(null);
  }, []);

  // Handle filter menu
  const handleFilterMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setFilterAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleFilterMenuClose = useCallback(() => {
    setFilterAnchorEl(null);
  }, []);

  const handleFileTypeFilterChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setFileTypeFilter(event.target.value as FileTypeFilter);
    },
    [],
  );

  const handleClearFilters = useCallback(() => {
    setFileTypeFilter("all");
    setDateFrom(null);
    setDateTo(null);
  }, []);

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (fileTypeFilter !== "all") count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    return count;
  }, [fileTypeFilter, dateFrom, dateTo]);

  // Check if sorting is active (not default)
  const isSortingActive = useMemo(() => {
    return (
      sortBy !== GetGeneticsReportsByPatientSortByEnum.UploadedAt ||
      orderBy !== GetGeneticsReportsByPatientOrderByEnum.Desc
    );
  }, [sortBy, orderBy]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Search and Filter Bar */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Search Input */}
          <TextField
            placeholder="Search by document name"
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            sx={{ flex: 1, minWidth: "250px" }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: colors.text.secondary }} />
                  </InputAdornment>
                ),
              },
            }}
          />

          {/* Sort Button */}
          <IconButton
            onClick={handleSortMenuOpen}
            sx={{
              border: `1px solid ${colors.border.main}`,
              borderRadius: 1,
              color: isSortingActive
                ? colors.primary.main
                : colors.text.secondary,
            }}
          >
            <Sort />
          </IconButton>

          {/* Filter Button */}
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={handleFilterMenuOpen}
              sx={{
                border: `1px solid ${colors.border.main}`,
                borderRadius: 1,
                color:
                  activeFiltersCount > 0
                    ? colors.primary.main
                    : colors.text.secondary,
              }}
            >
              <FilterList />
            </IconButton>
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  height: 20,
                  minWidth: 20,
                  bgcolor: colors.primary.main,
                  color: "white",
                  fontSize: "0.75rem",
                }}
              />
            )}
          </Box>
        </Box>

        {/* Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={isSortMenuOpen}
          onClose={handleSortMenuClose}
        >
          <MenuItem
            onClick={() =>
              handleSortChange(
                GetGeneticsReportsByPatientSortByEnum.UploadedAt,
                GetGeneticsReportsByPatientOrderByEnum.Desc,
              )
            }
            selected={
              sortBy === GetGeneticsReportsByPatientSortByEnum.UploadedAt &&
              orderBy === GetGeneticsReportsByPatientOrderByEnum.Desc
            }
          >
            Upload Date (Newest First)
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleSortChange(
                GetGeneticsReportsByPatientSortByEnum.UploadedAt,
                GetGeneticsReportsByPatientOrderByEnum.Asc,
              )
            }
            selected={
              sortBy === GetGeneticsReportsByPatientSortByEnum.UploadedAt &&
              orderBy === GetGeneticsReportsByPatientOrderByEnum.Asc
            }
          >
            Upload Date (Oldest First)
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleSortChange(
                GetGeneticsReportsByPatientSortByEnum.DocumentName,
                GetGeneticsReportsByPatientOrderByEnum.Asc,
              )
            }
            selected={
              sortBy === GetGeneticsReportsByPatientSortByEnum.DocumentName &&
              orderBy === GetGeneticsReportsByPatientOrderByEnum.Asc
            }
          >
            Document Name (A-Z)
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleSortChange(
                GetGeneticsReportsByPatientSortByEnum.DocumentName,
                GetGeneticsReportsByPatientOrderByEnum.Desc,
              )
            }
            selected={
              sortBy === GetGeneticsReportsByPatientSortByEnum.DocumentName &&
              orderBy === GetGeneticsReportsByPatientOrderByEnum.Desc
            }
          >
            Document Name (Z-A)
          </MenuItem>
        </Menu>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchorEl}
          open={isFilterMenuOpen}
          onClose={handleFilterMenuClose}
          PaperProps={{
            sx: {
              width: 320,
              p: 2,
            },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Filters
            </Typography>

            {/* File Type Filter */}
            <FormControl fullWidth size="small">
              <InputLabel>File Type</InputLabel>
              <Select
                value={fileTypeFilter}
                onChange={handleFileTypeFilterChange}
                label="File Type"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value={GetGeneticsReportsByPatientFileTypeEnum.Pdf}>
                  PDF
                </MenuItem>
                <MenuItem value={GetGeneticsReportsByPatientFileTypeEnum.Word}>
                  Word
                </MenuItem>
                <MenuItem value={GetGeneticsReportsByPatientFileTypeEnum.Image}>
                  Image
                </MenuItem>
              </Select>
            </FormControl>

            {/* Date Range Filter */}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Upload Date Range
            </Typography>
            <DatePicker
              label="From Date"
              value={dateFrom}
              onChange={(newValue) => setDateFrom(newValue)}
              maxDate={new Date()}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />
            <DatePicker
              label="To Date"
              value={dateTo}
              onChange={(newValue) => setDateTo(newValue)}
              maxDate={new Date()}
              minDate={dateFrom || undefined}
              slotProps={{
                textField: {
                  size: "small",
                  fullWidth: true,
                },
              }}
            />

            {/* Clear Filters Button */}
            {activeFiltersCount > 0 && (
              <Box
                onClick={handleClearFilters}
                sx={{
                  cursor: "pointer",
                  color: colors.primary.main,
                  textAlign: "center",
                  mt: 1,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                <Typography variant="body2">Clear All Filters</Typography>
              </Box>
            )}
          </Box>
        </Menu>

        {/* Total Results */}
        {!isLoading && !error && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {totalResults} {totalResults === 1 ? "result" : "results"} found
            </Typography>
          </Box>
        )}

        {/* Reports List */}
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Typography variant="body1" sx={{ color: colors.text.secondary }}>
              Loading...
            </Typography>
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Typography variant="body1" sx={{ color: colors.error[500] }}>
              Error loading genetics reports
            </Typography>
          </Box>
        ) : reports.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Typography variant="body1" sx={{ color: colors.text.secondary }}>
              No genetics reports found
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {reports.map((report: GeneticsReportResponseDto) => (
              <Card
                key={report.id}
                sx={{
                  border: `1px solid ${colors.border.main}`,
                  borderRadius: 2,
                  "&:hover": {
                    boxShadow: 2,
                    cursor: "pointer",
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {/* File Type Icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        bgcolor: colors.background.paper,
                      }}
                    >
                      {getFileTypeIcon(report.fileType)}
                    </Box>

                    {/* Document Info */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: colors.text.primary,
                          mb: 0.5,
                        }}
                      >
                        {report.documentName}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={getFileTypeLabel(report.fileType)}
                          size="small"
                          sx={{
                            bgcolor: colors.background.default,
                            color: colors.text.secondary,
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: colors.text.secondary,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatFileSize(report.fileSize)}
                          </Typography>
                        </Box>
                        {report.uploadedAt && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: colors.text.secondary,
                            }}
                          >
                            <CalendarToday sx={{ fontSize: 16 }} />
                            <Typography variant="body2">
                              {format(
                                new Date(report.uploadedAt),
                                "MMM dd, yyyy 'at' h:mm a",
                              )}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Download Button */}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(report.fileUrl, report.documentName);
                      }}
                      sx={{
                        border: `1px solid ${colors.border.main}`,
                        borderRadius: 1,
                        color: colors.text.secondary,
                      }}
                    >
                      <Download />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default PatientGenetics;
